import React, { useState } from 'react';
import { adminListUsers, setUserRole } from '../utils/api';
import './AdminPage.css';

const ROOT_ADMIN = 'wjv13';

/**
 * Admin console. The role flag in localStorage only reveals the menu entry —
 * every action here is re-verified server-side with the admin's password,
 * which is requested once when the console is opened and held in memory only.
 */
function AdminPage({ currentUser, onClose }) {
  const [adminPassword, setAdminPassword] = useState('');
  const [unlocked, setUnlocked] = useState(false);
  const [users, setUsers] = useState([]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const loadUsers = async (pw) => {
    setBusy(true);
    setError('');
    try {
      const res = await adminListUsers(currentUser.username, pw);
      setUsers(res.users || []);
      setUnlocked(true);
    } catch (e) {
      setError(e.message || 'Could not verify admin credentials');
    } finally {
      setBusy(false);
    }
  };

  const changeRole = async (target, newRole) => {
    setBusy(true);
    setError('');
    try {
      await setUserRole(currentUser.username, adminPassword, target, newRole);
      await loadUsers(adminPassword);
    } catch (e) {
      setError(e.message || 'Role change failed');
      setBusy(false);
    }
  };

  if (!unlocked) {
    return (
      <div className="admin-overlay">
        <div className="admin-card admin-unlock">
          <button className="admin-close" onClick={onClose}>×</button>
          <h2>Admin Console</h2>
          <p>Re-enter your password to continue.</p>
          <input
            type="password"
            autoFocus
            placeholder="Your password"
            value={adminPassword}
            onChange={e => setAdminPassword(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && adminPassword && loadUsers(adminPassword)}
          />
          {error && <p className="admin-error">{error}</p>}
          <button
            className="admin-btn"
            disabled={busy || !adminPassword}
            onClick={() => loadUsers(adminPassword)}
          >
            Unlock
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-overlay">
      <div className="admin-card admin-console">
        <button className="admin-close" onClick={onClose}>×</button>
        <h2>Admin Console</h2>
        {error && <p className="admin-error">{error}</p>}

        <section>
          <h3>Users</h3>
          <table className="admin-table">
            <thead>
              <tr>
                <th>Username</th><th>Name</th><th>Email</th>
                <th>Role</th><th>Created</th><th></th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.username}>
                  <td>@{u.username}</td>
                  <td>{[u.firstName, u.lastName].filter(Boolean).join(' ') || '—'}</td>
                  <td>{u.email || '—'}</td>
                  <td>
                    <span className={`role-pill ${u.role === 'admin' ? 'admin' : ''}`}>
                      {u.role}
                    </span>
                  </td>
                  <td>{(u.createdAt || '').slice(0, 10)}</td>
                  <td>
                    {u.username === ROOT_ADMIN ? (
                      <span className="root-tag" title="Root admin cannot be demoted">root</span>
                    ) : u.role === 'admin' ? (
                      <button className="admin-btn small revoke" disabled={busy}
                              onClick={() => changeRole(u.username, 'user')}>
                        Revoke admin
                      </button>
                    ) : (
                      <button className="admin-btn small" disabled={busy}
                              onClick={() => changeRole(u.username, 'admin')}>
                        Grant admin
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        <section className="admin-radar">
          <h3>⚠️ Radar chart needs rework</h3>
          <p>
            The radar chart was removed from public results and is now admin-only.
            Run a search on the home page while logged in as an admin — the
            “Show Radar Chart” button appears under the results for admins.
            This visualization is slated for a redesign.
          </p>
        </section>
      </div>
    </div>
  );
}

export default AdminPage;
